/**
 * HTTP client + normalization for the migrations PORTFOLIO PASS.
 *
 * Five routes exist and no more. One is free (`/inventory`); four are entitlement-gated
 * and refuse with a 403 carrying the same body shape as the inventory's `triage` block.
 *
 * THE 403 IS A FIRST-CLASS RESULT, NOT AN EXCEPTION TO REPORT. The disabled button on the
 * screen is a courtesy; the SERVER is the entitlement control, and it will refuse a paid
 * call whether or not the client thought it should. So `portfolioRefusal` turns any 403
 * back into the same `PortfolioTriage` the free route hands out, and the screen renders
 * the identical contact path it would have rendered had it never offered the button. A
 * customer who reaches a paid route by any means — a stale tab, a re-enabled button, a
 * direct call — gets told how to buy it, not a stack trace.
 *
 * Counts are normalized to `PORTFOLIO_COUNT_UNKNOWN` and never to `0`. See `./types`.
 */
import { apiGet, apiPost, ApiError } from "@/services/studio/client";
import {
  asRecord,
  humanize,
  numberOrNull,
  numberValue,
  stringOrNull,
  stringValue,
} from "@/services/studio/primitives";
import type {
  PortfolioByObject,
  PortfolioCluster,
  PortfolioCounts,
  PortfolioEstimate,
  PortfolioInventory,
  PortfolioObjectRow,
  PortfolioPassCreated,
  PortfolioPassRequest,
  PortfolioPassRunResult,
  PortfolioPassTriage,
  PortfolioTriage,
} from "./types";
import { PORTFOLIO_COUNT_UNKNOWN, PORTFOLIO_DISABLED } from "./types";

export * from "./types";

const BASE = "/api/migrations/portfolio";

// ── reads and writes ────────────────────────────────────────────────────────────

/**
 * The FREE route. Works for an org that has bought nothing, which is the entire point of
 * it: this is the surface that shows a customer their own scale before they buy anything.
 */
export async function getPortfolioInventory(): Promise<PortfolioInventory> {
  return normalizeInventory(await apiGet<unknown>(`${BASE}/inventory`));
}

/**
 * PAID. Takes no parameters — the server decides the scope and returns the one it chose,
 * which is then echoed back on `POST /passes` rather than picked here. A client that
 * chose its own scope would be quoting itself.
 */
export async function getPortfolioEstimate(): Promise<PortfolioEstimate> {
  return normalizeEstimate(await apiGet<unknown>(`${BASE}/estimate`));
}

/**
 * PAID. Creates the pass. `accept_estimate` is a STRICT boolean server-side, so it is
 * sent as the literal the caller passed and never as a truthy value — the same discipline
 * as the engine's `check_only: false`. A caller who has not shown a human the estimate
 * has no business passing `true`, and the call site that does is the confirm dialog.
 */
export async function createPortfolioPass(
  body: PortfolioPassRequest
): Promise<PortfolioPassCreated> {
  const raw =
    asRecord(
      await apiPost<unknown>(`${BASE}/passes`, {
        scope: body.scope,
        component_count: body.component_count,
        accept_estimate: body.accept_estimate === true,
        ...(body.inventory_sha ? { inventory_sha: body.inventory_sha } : {}),
        ...(body.title ? { title: body.title } : {}),
      })
    ) ?? {};
  return {
    plan_id: stringValue(raw.plan_id, ""),
    status: stringValue(raw.status, "unknown"),
  };
}

/** PAID. 202 — the pass runs asynchronously and the triage route is polled for it. */
export async function runPortfolioPass(planId: string): Promise<PortfolioPassRunResult> {
  const raw =
    asRecord(await apiPost<unknown>(`${BASE}/passes/${encodeURIComponent(planId)}/run`, {})) ?? {};
  return {
    plan_id: stringValue(raw.plan_id, planId),
    status: stringValue(raw.status, "unknown"),
  };
}

/** PAID. The clustered result: the shapes an org's automation actually falls into. */
export async function getPortfolioPassTriage(planId: string): Promise<PortfolioPassTriage> {
  return normalizePassTriage(
    await apiGet<unknown>(`${BASE}/passes/${encodeURIComponent(planId)}/triage`),
    planId
  );
}

// ── error classification ────────────────────────────────────────────────────────

/**
 * The refusal body, wherever it landed.
 *
 * FastAPI wraps `HTTPException(detail=...)` and returns the payload bare on other paths,
 * and `client.parseError` keeps whichever arrived verbatim. Both spellings are read here
 * — not as a hedge against an unknown contract, but because this app's own HTTP client
 * demonstrably produces both (see `engineErrorMessage`, which does the same).
 */
export function portfolioErrorPayload(error: unknown): Record<string, unknown> | null {
  if (!(error instanceof ApiError)) return null;
  const body = asRecord(error.body);
  if (!body) return null;
  return asRecord(body.detail) ?? body;
}

/**
 * This deployment has no portfolio API at all. Not an error to surface — the surface
 * hides itself, exactly as `isEngineAbsent` does for the engine.
 */
export function isPortfolioAbsent(error: unknown): boolean {
  if (!(error instanceof ApiError)) return false;
  return error.status === 404 || error.status === 405 || error.status === 501;
}

/**
 * A paid route refused for entitlement.
 *
 * ANY 403 counts, not only one carrying `PORTFOLIO_NOT_ENTITLED`. Entitlement is the only
 * thing these four routes refuse on, and the failure mode of being wrong here is
 * asymmetric: reading an odd 403 as "not entitled" shows a contact path, while reading a
 * `PORTFOLIO_NOT_ENTITLED` whose code field was reworded as "unknown error" shows a
 * customer a stack trace on the screen that is meant to sell them something.
 */
export function isPortfolioNotEntitled(error: unknown): boolean {
  return error instanceof ApiError && error.status === 403;
}

/** `GET /inventory` reached an org it could not read. Distinct from every other failure. */
export function isOrgUnreadable(error: unknown): boolean {
  if (!(error instanceof ApiError) || error.status !== 502) return false;
  return stringOrNull(portfolioErrorPayload(error)?.error) === "org_unreadable";
}

/**
 * A 403 from a paid route, read back as the triage block the free route would have given.
 *
 * `available` and `entitled` are FORCED false rather than read: a refusal that claimed to
 * be entitled is not a claim worth carrying, and the status code already settled it.
 */
export function portfolioRefusal(error: unknown): PortfolioTriage | null {
  if (!isPortfolioNotEntitled(error)) return null;
  const triage = normalizeTriage(portfolioErrorPayload(error));
  return { ...triage, available: false, entitled: false };
}

/**
 * THIS DEPLOYMENT RUNS NO PORTFOLIO PASSES. Detected by CODE, not by status.
 *
 * `PORTFOLIO_DISABLED` arrives as a 409 from `POST /passes` (`authorise_run` raises layer
 * one before anything is planned) and it is a fundamentally different sentence from the
 * 403 above: the org may hold the entitlement and it changes nothing, because the
 * capability is not deployed here. So the code is what identifies it — a status match
 * would either miss it or swallow every other 409 the pass route can produce.
 *
 * Kept separate from `portfolioRefusal` rather than folded into it, because folding them
 * is exactly how this ends up rendering the contact path that `entitlements.py` argues at
 * length must accompany the OTHER refusal and that this one deliberately omits.
 */
export function isPortfolioDisabled(error: unknown): boolean {
  if (!(error instanceof ApiError)) return false;
  return stringOrNull(portfolioErrorPayload(error)?.code) === PORTFOLIO_DISABLED;
}

/**
 * The `PORTFOLIO_DISABLED` refusal, or `null`.
 *
 * `contact` is FORCED to `null` rather than read. The server sends none, and forcing it
 * here means a future payload that grew one by accident still cannot turn a flag that is
 * off into a purchase conversation.
 *
 * `entitled` IS NOT FORCED, and the asymmetry with `portfolioRefusal` above is the point.
 * There, a 403 settles the question — the status code IS the entitlement answer, so a body
 * claiming otherwise is not worth carrying. Here the status settles nothing of the kind:
 * this refusal is about what the deployment mounts, and the org may perfectly well hold the
 * entitlement. Writing `false` would be manufacturing a commercial fact out of a
 * configuration one, in the exact direction that sends a paying customer to sales.
 */
export function portfolioDisabledRefusal(error: unknown): PortfolioTriage | null {
  if (!isPortfolioDisabled(error)) return null;
  const triage = normalizeTriage(portfolioErrorPayload(error));
  return { ...triage, available: false, contact: null };
}

export function portfolioErrorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    const message = stringOrNull(portfolioErrorPayload(error)?.message);
    if (message) return message;
    return error.message || `Request failed (${error.status})`;
  }
  return error instanceof Error ? error.message : "Request failed";
}

// ── normalization ───────────────────────────────────────────────────────────────

/** A count the server sent, or UNKNOWN. Never `0` — see the note on the constant. */
function countValue(value: unknown): number {
  return numberOrNull(value) ?? PORTFOLIO_COUNT_UNKNOWN;
}

export function normalizeCounts(input: unknown): PortfolioCounts {
  const raw = asRecord(input) ?? {};
  return {
    workflow_rules: countValue(raw.workflow_rules),
    objects: countValue(raw.objects),
    profiles: countValue(raw.profiles),
    addressable: countValue(raw.addressable),
    unaddressable: countValue(raw.unaddressable),
  };
}

/**
 * Both wire shapes of `triage`, into one.
 *
 * `available` is `true` only on a JSON `true`. An absent triage block, a malformed one,
 * or one this normalizer cannot read all resolve to "not available" — the direction in
 * which being wrong costs a sales conversation rather than an unauthorised run.
 */
export function normalizeTriage(input: unknown): PortfolioTriage {
  const raw = asRecord(input) ?? {};
  return {
    available: raw.available === true,
    entitled: raw.entitled === true,
    code: stringOrNull(raw.code),
    message: stringOrNull(raw.message),
    reason: stringOrNull(raw.reason),
    contact: stringOrNull(raw.contact),
  };
}

/**
 * The per-object breakdown. Rows this client cannot read are COUNTED, not dropped.
 *
 * `{"object": name, "rules": n}` — `inventory.py::by_object()`. The second key is `rules`
 * and NOT `count`; reading `count` made every row of a real 69-object org unreadable.
 * Only the confirmed spelling is read: a normalizer that accepted both would be a branch
 * no test can fail and would have hidden exactly this bug.
 *
 * The `unreadable` counter is what turned that into a one-line fix. A row needs a name
 * and a count to be a row; anything else increments the counter and the panel prints one
 * line saying so, rather than rendering a short table that reads like a small org.
 */
export function normalizeByObject(input: unknown): PortfolioByObject {
  if (!Array.isArray(input)) return { rows: [], unreadable: 0 };
  const rows: PortfolioObjectRow[] = [];
  let unreadable = 0;
  for (const item of input) {
    const raw = asRecord(item);
    const object = raw ? stringOrNull(raw.object) : null;
    const rules = raw ? numberOrNull(raw.rules) : null;
    if (!object || rules == null) {
      unreadable += 1;
      continue;
    }
    rows.push({ object, rules });
  }
  return { rows, unreadable };
}

export function normalizeInventory(payload: unknown): PortfolioInventory {
  const raw = asRecord(payload) ?? {};
  return {
    org_id: stringValue(raw.org_id, ""),
    api_version: stringValue(raw.api_version, ""),
    route: stringValue(raw.route, ""),
    // Absent reads as INCOMPLETE. A completeness claim the server did not make is not
    // one this client may make on its behalf.
    complete: raw.complete === true,
    pages_read: numberValue(raw.pages_read, 0),
    counts: normalizeCounts(raw.counts),
    by_object: normalizeByObject(raw.by_object),
    triage: normalizeTriage(raw.triage),
  };
}

export function normalizeEstimate(payload: unknown): PortfolioEstimate {
  const raw = asRecord(payload) ?? {};
  return {
    org_id: stringValue(raw.org_id, ""),
    scope: stringValue(raw.scope, ""),
    counts: normalizeCounts(raw.counts),
    inventory_sha: stringValue(raw.inventory_sha, ""),
    // Carried, not interpreted: the field list is undocumented and this is a price.
    estimate: asRecord(raw.estimate) ?? {},
  };
}

/**
 * One cluster. `label`/`count` where the server sent them; every other scalar it sent is
 * carried through verbatim as a labelled pair rather than discarded for being unfamiliar.
 */
export function normalizeCluster(input: unknown, index: number): PortfolioCluster {
  const raw = asRecord(input) ?? {};
  const count = numberOrNull(raw.count);
  const fields: Array<{ label: string; value: string }> = [];
  for (const [key, value] of Object.entries(raw)) {
    if (key === "key" || key === "label" || key === "count") continue;
    if (typeof value !== "string" && typeof value !== "number" && typeof value !== "boolean") {
      continue;
    }
    fields.push({ label: humanize(key), value: String(value) });
  }
  return {
    key: stringValue(raw.key, `cluster-${index}`),
    label: stringValue(raw.label, stringValue(raw.key, `Shape ${index + 1}`)),
    count: count ?? PORTFOLIO_COUNT_UNKNOWN,
    known: count != null,
    fields,
  };
}

export function normalizePassTriage(payload: unknown, fallbackPlanId: string): PortfolioPassTriage {
  const raw = asRecord(payload) ?? {};
  const counts: Record<string, number> = {};
  for (const [key, value] of Object.entries(asRecord(raw.counts) ?? {})) {
    const count = numberOrNull(value);
    if (count != null) counts[key] = count;
  }
  return {
    plan_id: stringValue(raw.plan_id, fallbackPlanId),
    status: stringValue(raw.status, "unknown"),
    counts,
    clusters: (Array.isArray(raw.clusters) ? raw.clusters : []).map(normalizeCluster),
  };
}
